import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountSuccess - The success element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountSuccess'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;
    const normalizedDiscountCodeValue = discountCodeValue.toLowerCase();

    const abortController = this.#createAbortController();
    const sectionId = this.dataset.sectionId;

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => code.toLowerCase() === normalizedDiscountCodeValue)) {
        this.#handleDiscountSuccess();
        return;
      }

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      this.#hideDiscountSuccess();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const appliedDiscount = data.discount_codes.find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code.toLowerCase() === normalizedDiscountCodeValue && discount.applicable === true;
        }
      );

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code.toLowerCase() === normalizedDiscountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError();
        return;
      }

      const newHtml = data.sections[sectionId];

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(sectionId, newHtml);

      CartDiscount.#expandDiscountDisclosure(sectionId);
      if (appliedDiscount) {
        CartDiscount.#showDiscountSuccess(sectionId);
      }
    } catch (error) {
      if (!(error instanceof DOMException) || error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      if (!(error instanceof DOMException) || error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }
  };

  /**
   * Handles the discount error.
   *
   */
  #handleDiscountError() {
    const { cartDiscountError, cartDiscountErrorDiscountCode } = this.refs;
    cartDiscountError.classList.remove('hidden');
    cartDiscountErrorDiscountCode.classList.remove('hidden');
  }

  #handleDiscountSuccess() {
    this.refs.cartDiscountError.classList.add('hidden');
    this.refs.cartDiscountErrorDiscountCode.classList.add('hidden');
    this.refs.cartDiscountSuccess.classList.remove('hidden');
    this.#setCurrentDisclosureExpanded(true);
  }

  #hideDiscountSuccess() {
    this.refs.cartDiscountSuccess.classList.add('hidden');
  }

  /**
   * @param {boolean} expanded
   */
  #setCurrentDisclosureExpanded(expanded) {
    const disclosure = this.closest('disclosure-custom');
    if (disclosure instanceof HTMLElement) {
      CartDiscount.#setDisclosureExpanded(disclosure, expanded);
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * @param {string} sectionId
   */
  static #expandDiscountDisclosure(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    const disclosure = section?.querySelector('disclosure-custom.cart-discount');
    if (disclosure instanceof HTMLElement) {
      CartDiscount.#setDisclosureExpanded(disclosure, true);
    }
  }

  /**
   * @param {string} sectionId
   */
  static #showDiscountSuccess(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    section?.querySelector('[ref="cartDiscountSuccess"]')?.classList.remove('hidden');
  }

  /**
   * @param {HTMLElement} disclosure
   * @param {boolean} expanded
   */
  static #setDisclosureExpanded(disclosure, expanded) {
    const trigger = disclosure.querySelector('[ref="disclosureTrigger"]');
    const content = disclosure.querySelector('[ref="disclosureContent"]');

    disclosure.toggleAttribute('data-expanded', expanded);

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', String(expanded));
      trigger.setAttribute('data-expanded', String(expanded));
      trigger.setAttribute(
        'aria-label',
        expanded
          ? trigger.dataset.disclosureClose || trigger.getAttribute('aria-label') || ''
          : trigger.dataset.disclosureOpen || trigger.getAttribute('aria-label') || ''
      );
    }

    if (content instanceof HTMLElement) {
      content.setAttribute('data-expanded', String(expanded));
      content.toggleAttribute('inert', !expanded);
      content.inert = !expanded;
    }
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
